/**
 * English source dictionary — the single place every user-facing string in the
 * app is written down (SPEC §4: "English only, i18n-ready — every user-facing
 * string goes through `t()` from day one").
 *
 * Rules for anyone adding keys:
 *  - Group by feature: `common`, `nav`, `footer`, `parcel`, `checkout`,
 *    `myFarm`, `admin`, `errors`, `seo`. Add a new top-level group only for a
 *    new feature area.
 *  - Keys are camelCase, values are sentence-case English. Do NOT pre-uppercase
 *    text for the design — headings are uppercased in CSS, so the source string
 *    stays readable and translatable.
 *  - Interpolate with `{{name}}`: `t('parcel.updatesCount', { count: 4 })`.
 *  - Never concatenate translated fragments; add a full sentence key instead.
 *
 * `as const` is load-bearing: it is what makes `t()` key-checked at compile time.
 */
export const en = {
  common: {
    brand: "Kuebefarm",
    brandTagline: "Urban farm · Kirchberg, Luxembourg",
    loading: "Loading",
    loadingLong: "Still working — this is taking longer than usual.",
    retry: "Try again",
    close: "Close",
    cancel: "Cancel",
    confirm: "Confirm",
    back: "Back",
    next: "Next",
    previous: "Previous",
    viewAll: "View all",
    learnMore: "Learn more",
    readMore: "Read more",
    showLess: "Show less",
    copy: "Copy",
    copied: "Copied",
    page: "Page {{page}} of {{totalPages}}",
    resultCount: "{{count}} results",
    resultCountOne: "1 result",
    optional: "Optional",
    skipToContent: "Skip to content",
    openInNewTab: "Opens in a new tab",
    imageUnavailable: "Image unavailable",
    /** Placeholder screens only — delete once every container is built. */
    comingSoon: "This screen is being built.",
  },

  nav: {
    home: "Home",
    parcels: "The book",
    myFarm: "My farm",
    admin: "Operations",
    login: "Log in",
    logout: "Log out",
    account: "Account",
    accountMenu: "Account menu",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    signedInAs: "Signed in as",
    wallet: "Wallet",
    copyWallet: "Copy your wallet address",
  },

  landing: {
    // The public front door. A visitor arrives here from kuebefarm.lu, so the
    // voice is the farm's, not a marketplace's: plain, specific, unhurried.
    // Nothing here may promise land, title or produce — see
    // docs/LEGAL-BLOCKERS.md. The "And what it is not" disclaimer that used to
    // carry that limit explicitly was removed by the client on 2026-07-29; the
    // burden now falls on every string in this block staying inside it.
    // The hero produce. Decorative — the whole layer is `aria-hidden`, so
    // these never reach a screen reader; they exist so the markup still says
    // what each file is if the images fail or the layer is ever un-hidden.
    produceArtichokes: "Jerusalem artichokes",
    produceLettuce: "A head of loose-leaf lettuce",
    produceChard: "A chard leaf with a pink stem",
    produceLeaf: "A young salad leaf",
    produceSage: "A sage leaf",
    heroTitle: "Be a part of Kuebefarm",
    heroLead:
      "Kuebefarm works as a market garden on the Kuebebierg, above Kirchberg. Everyone who helps keep it going gets a portrait of their own, with a name, and only one person ever has it. Pick yours and the season comes to you, sent up by the people who grow it.",
    heroCta: "See what is available",
    heroScroll: "How it works",

    howEyebrow: "How it works",
    howTitle: "Three steps, then a season of watching",
    step1Title: "Choose yours",
    step1Body:
      "Every Kuebefarm membership is one of a kind — drawn once, named once, and belonging to one member only. Look through the collection and pick the one that feels like yours.",
    step2Title: "Become a member",
    step2Body:
      "One payment covers your year as a member of Kuebefarm a.s.b.l. Your name goes in the member book.",
    step3Title: "Be part of it",
    step3Body:
      "You're part of Kuebefarm now. Come along to our events, meet the people and the animals, and take part in the life of the project as it grows, right here on the Kirchberg plateau.",

    featuredEyebrow: "Free right now",
    featuredTitle: "NFTs looking for a member",
    featuredLead:
      "A few NFTs nobody has taken yet. The full list is in the book catalogue.",
    // Inside the NFT section, so it takes the section's word — see the note on
    // `parcel.viewNft`. The link still goes to `/memberships`.
    featuredAll: "See every NFT",
    featuredEmptyTitle: "Every membership is taken",
    featuredEmptyBody:
      "The whole farm is spoken for this season. New memberships are drawn as they are prepared — write to us and we will tell you first.",

    receiveEyebrow: "Being plain about it",
    receiveTitle: "A membership, a record, and the season as it happens",
    receiveLead:
      "Three things change hands, and it is worth saying exactly what they are.",
    receive1Title: "A named membership",
    receive1Body:
      "Your name goes in the book of people who keep this farm on the Kuebebierg going. Each membership is drawn once.",
    receive2Title: "A certificate, recorded on Polygon",
    receive2Body:
      "Your membership and every update posted to it are written to a public ledger, so the record cannot quietly change afterwards. It is a receipt, not a deed: proof of your place here, kept where no one can edit it.",
    receive3Title: "A year at the farm through the seasons",
    receive3Body:
      "This is a journey you're part of, not a feed you follow. All season, photographs and notes come straight from the field, written by you and the people doing the work. You are invited to live it up close: come to our events, meet the community, and take part in the life of the project on the Kirchberg plateau.",

    // The statement band between what a membership is and what it does.
    teaserTitle:
      "We are building the future of Kuebefarm through its tokens",

    // What the membership is actually good for, in the member's hands. Four
    // plain statements, no titles — each one is its own sentence and the list
    // is read straight down.
    utilityEyebrow: "Utility",
    utilityTitle: "What your membership does",
    utility1:
      "Vegetables reach members before the stand. Scarce crops go to the list first.",
    utility2:
      "Come and eat. Book the long table for a meal, or the whole farm for the day.",
    utility3:
      "Beer and food too. Spend it at the brewhouse and in the kitchen, not just on vegetables.",
    utility4: "Patron, a tree, a hive or an animal, and the story of its year.",

    // ⚠️ Reads as a forward-looking statement about an unissued instrument.
    // Keep it in the future tense and keep "soon to be disclosed" — this page
    // must not read as an offer of the security token. See
    // docs/LEGAL-BLOCKERS.md before rewording.
    securityTitle: "Security",
    securityBody:
      "The land, the buildings and the trees will be financed separately, through a dedicated security token, soon to be disclosed. It is a different instrument with a different purpose: the membership carries your year at the farm; the security token will carry the ground itself.",

    closingTitle: "The season starts whether or not you are watching",
    closingBody: "Memberships open now.",
    closingCta: "Become a member now",
  },

  footer: {
    // ⚠️ LEGAL — see docs/LEGAL-BLOCKERS.md before touching this block.
    //
    // The live kuebefarm.lu footer is unedited WordPress theme demo content:
    // a Swiss address, and social links pointing at the *theme vendor's* own
    // accounts. None of it may be reused (LEGAL-BLOCKERS §1). The site also
    // publishes no legal notice, T&Cs, privacy, cookie or refund policy
    // (§2) — a hard blocker for both Stripe live mode and EU consumer law.
    //
    // Only the values verified in LEGAL-BLOCKERS §3 appear below. Everything
    // else is a TODO carrying its checklist number, so `grep TODO` lists
    // exactly what is still owed by the client. Deliberately absent:
    // social links (§3 checklist #14) and opening hours (§4 — the site's two
    // versions contradict each other; we publish none rather than guess).
    tagline: "A working urban farm on the Kuebebierg, Kirchberg plateau.",
    explore: "Explore",
    legal: "Legal",
    contact: "Contact",
    // The next four come from clause 1.3 of the Terms and Conditions the
    // client supplied (LEGAL-BLOCKERS §6), which is what finally settled
    // §3's open question of which of the two entities sells. Confirmed for
    // publication — they print as fact, so they may not be edited from any
    // other source.
    entityName: "Kuebefarm a.s.b.l.",
    // The farm, and where a visitor goes — LEGAL-BLOCKERS §3 (contact page).
    // Deliberately not the same address as `registeredOffice` below: §3
    // flagged the two as a contradiction to resolve, and clause 1.3 resolved
    // it by showing they are simply different things.
    address: "139 Fond Saint-Martin, Luxembourg",
    registeredOffice: "Registered office: 40, rue Glesener, L-1630 Luxembourg",
    registration: "RCS Luxembourg F15107",
    // TODO(legal #4): the one imprint field the Terms and Conditions did not
    // answer. Clause 9.1 prices the membership "inclusive of any applicable
    // value added tax" without giving a number, so this still has to come
    // from the client — as a VAT number or as written confirmation that the
    // ASBL is not registered.
    vat: "TODO — VAT number",
    // Verified, LEGAL-BLOCKERS §3.
    email: "Moien@kuebefarm.lu",
    imprint: "Legal notice",
    terms: "Terms & conditions",
    privacy: "Privacy policy",
    cookies: "Cookie policy",
    refunds: "Refund policy",
    legalPending: "Pending — not yet published",
    rights: "© {{year}} Kuebefarm. All rights reserved.",
    poweredBy: "Membership records secured by Filedgr",
  },

  parcel: {
    // Catalogue
    catalogueTitle: "Memberships",
    catalogueEyebrow: "The book",
    catalogueIntro:
      "Every membership is drawn once and named once. One to a person, and it stays with them.",
    filterLabel: "Filter by availability",
    filterAll: "All",
    filterAvailable: "Available",
    filterReserved: "Reserved",
    filterSold: "Sold",
    emptyTitle: "No memberships are listed yet",
    emptyBody:
      "The farm draws memberships at the start of each growing season. Check back shortly, or write to us to be told first.",
    filteredEmptyTitle: "Nothing under this filter",
    filteredEmptyBody:
      "No membership currently has that status. Try another one, or look at the whole book.",
    filteredEmptyAction: "Show every membership",
    notFoundTitle: "That membership does not exist",
    notFoundBody:
      "The link may be out of date, or the membership was withdrawn from the book.",
    backToCatalogue: "Back to all memberships",
    loadMore: "Load more memberships",
    showingCount: "Showing {{shown}} of {{total}}",
    viewParcel: "View this membership",
    /**
     * The landing strip calls the same object an NFT rather than a membership
     * — that section is addressed to someone who has not read anything else on
     * the page yet, and the word is the one they arrive with. Everywhere past
     * the front door it is a membership. Same card, `ctaKey` picks the label.
     */
    viewNft: "View this NFT",
    imageAlt: "{{name}}, photographed at Kuebefarm",

    // Status
    status: {
      available: "Available",
      reserved: "Reserved",
      sold: "Taken",
    },
    statusReservedNote:
      "Someone is checking out right now. If they do not finish, it returns to the book within 30 minutes.",
    statusSoldNote: "This membership has a member for the season.",

    // Detail
    aboutEyebrow: "The member",
    // A vault description is one free-text box and most are short or empty, so
    // the About block has to stand on its own when there is barely anything in
    // it — a heading with one line under it beside a tall purchase panel reads
    // as broken. These point the reader at the thing that IS substantial.
    //
    // Once a membership is taken this box holds the member: the year they
    // joined and a short note about them, written by hand after the sale. Until
    // then it is empty, which is what `descriptionNone` covers.
    descriptionNone: "This member has not been written up yet.",
    descriptionUpdatesLead:
      "What there is to see is in the field updates — {{count}} so far, written and photographed as the work happens.",
    descriptionUpdatesLeadOne:
      "What there is to see is in the field updates — one so far, written and photographed as the work happens.",
    descriptionNoUpdatesYet:
      "Nothing has been posted from the field yet. The first update usually lands within a fortnight of sowing, and every one after it appears here.",
    seeUpdates: "Read the field updates",
    about: "About this member",
    price: "Price",
    priceOnRequest: "Price on request",
    oneOff: "One-off, one season",
    updates: "Field updates",
    updatesEyebrow: "From the farm",
    updatesCount: "{{count}} updates",
    updatesCountOne: "1 update",
    updatesNone: "No updates yet",
    updatesNoneBody:
      "The farm posts photographs and notes as the season is planted, tended and harvested. The first one usually lands within a fortnight of sowing.",
    updatesLoadMore: "Load older updates",
    updateImageAlt: "{{title}} — photograph {{index}} of {{total}}",

    // The archive. Only the newest post is open; everything before it is a
    // dated list you click to read in place.
    updatesEarlier: "Earlier from this membership",
    updatesJump: "Jump to",

    // The ledger detail on a post, kept behind a toggle. Whoever bought a
    // vegetable bed should never have to look at a hash; whoever wants to
    // check should never have to ask us for one.
    // Not "Recorded on the ledger" — that only says where the post was filed.
    // The point of filing it is that the post is now fixed: the farm can write
    // a later correction, but this one cannot be quietly rewritten. Note the
    // claim is about *this* post, which is what the ledger actually guarantees;
    // do not inflate it to "nothing here ever changes".
    updateRecorded: "Recorded — this post cannot be changed",
    updateDetails: "Details",
    updateRecordLabel: "Update record",
    viewAttachment: "Open on the explorer",
    transactionId: "Full hash",

    record: "On-chain record",
    recordNote:
      "Each membership and every update is written to a public ledger, so the history cannot be quietly rewritten.",
    registeredOn: "Registered",
    transaction: "Transaction",
    // NOT a network — it is the ERC-721 token id off the vault's
    // `nft_network_id`, 76-77 digits on real data. filedgr-web-app labels the
    // same field "Token ID". SPEC §11 fixes the network to POLYGON_ZKEVM, so
    // there is only ever one and it is not worth a column.
    tokenId: "Token ID",
    ledgerLabel: "Ledger",
    viewOnExplorer: "View the record",
    txPending: "Not yet written to the ledger",
    // What the member sees on their own page. The catalogue states ("Taken",
    // "No longer available") are true of everyone else and cold nonsense to
    // the person who paid for it.
    ownedBadge: "Yours",
    ownedSince: "Yours since {{date}}",
    ownedGoToMyFarm: "See it in my farm",

    buy: "Become a member",
    buyUnavailable: "No longer available",
    // Matches `checkout.redirecting` below — one voice for the same moment,
    // and shorter than the label it replaces so the button never has to grow.
    buyPreparing: "Taking you to payment",
    loginToBuy: "Log in to become a member",
    loginToBuyNote:
      "We need an account before checkout so the membership record can be shared with you the moment payment clears.",
  },

  checkout: {
    title: "Checkout",
    redirecting: "Taking you to secure payment",
    redirectingNote: "Payment is handled by Stripe. Do not close this window.",
    returnTitle: "Confirming your membership",
    returnPending:
      "Payment received. Registering the membership to your account.",
    returnGranted: "Done. The membership is yours.",
    returnGrantedBody:
      "You will find it under My farm, along with every update the farm posts.",
    returnFailedTitle: "Payment went through, registration did not",
    returnFailedBody:
      "Your payment is safe and the membership is held for you. We have been notified and will finish the registration by hand — usually within a few hours. Nothing further is needed from you.",
    returnCancelledTitle: "Checkout cancelled",
    returnCancelledBody:
      "Nothing was charged and the membership has gone back into the book.",
    goToMyFarm: "Go to my farm",
    keepBrowsing: "Keep browsing memberships",
    orderReference: "Order reference",

    // --- Return screen -----------------------------------------------------
    // Payment and the viewer grant settle independently (SPEC §7), so they get
    // separate words throughout. A paid order whose grant is still pending is a
    // SUCCESS: under `GRANT_STRATEGY=manual` a person at the farm switches
    // access on by hand, and the copy below must never imply a fault.
    returnEyebrow: "Your order",
    confirmingTitle: "Confirming your payment",
    confirmingBody:
      "This usually takes a few seconds. Keep this page open and it will update itself.",
    accessPendingTitle: "Paid. The membership is yours.",
    accessPendingBody:
      "Access to the membership record is switched on by someone at the farm, usually the same day. We will email you the moment it is ready — there is nothing else for you to do.",
    stillWorkingTitle: "Still confirming, quietly",
    stillWorkingBody:
      "Your payment is with the bank and nothing has gone wrong. We will email you a receipt as soon as it clears, so you can safely close this page.",
    accessFailedNote:
      "Your money is safe and the membership is held in your name. Please do not pay again.",
    missingSessionTitle: "We could not find that order",
    missingSessionBody:
      "This link is missing its order reference. If you have paid, the membership is waiting under My farm and a receipt is on its way by email.",
    lookupFailedTitle: "We could not load your order just now",
    lookupFailedBody:
      "Your payment is unaffected. Try again in a moment, or open My farm — the membership appears there as soon as it is registered.",
    checkAgain: "Check again",
    stepPayment: "Payment",
    stepAccess: "Membership access",
    statusConfirming: "Confirming",
    statusReceived: "Received",
    statusAccessPending: "Being set up",
    statusAccessGranted: "Ready",
    statusAccessFailed: "With the team",
    parcelLabel: "Membership",
    amountLabel: "Paid",
    emailedTo: "Receipt on its way to {{email}}",
    contactTitle: "If you would rather ask a person",
    contactBody:
      "Write to {{email}} quoting the order reference above and someone will answer you directly.",
    viewParcel: "See the membership",
    // Announced to screen readers as the two statuses settle.
    liveConfirming: "Confirming your payment.",
    livePaid: "Payment received.",
    liveGranted: "Payment received and your access is ready.",
    livePending: "Payment received. Access to the membership is being set up.",
    liveFailed:
      "Payment received. The team is finishing the registration by hand.",
    legalEyebrow: "The small print",

    // ⚠️ LEGAL — the receipt's small print. Read docs/LEGAL-BLOCKERS.md §6
    // before touching any of it.
    //
    // The first two are settled and taken straight from the Terms and
    // Conditions: the seller from clause 1.3, the price and VAT position from
    // clause 9.1. They are quotations, not copy — reword them and the receipt
    // stops matching the contract it is a receipt for.
    soldBy:
      "Sold by Kuebefarm a.s.b.l., 40, rue Glesener, L-1630 Luxembourg — RCS Luxembourg F15107.",
    // Clause 9.1 verbatim on the VAT position. Note this states what the price
    // INCLUDES, which is the disclosure duty; the VAT *number* is still owed
    // (checklist #4) and belongs in the imprint, not here.
    vatNote: "€50.00, inclusive of any applicable value added tax.",

    // ⚠️ These three are still placeholders, and deliberately so — see
    // LEGAL-BLOCKERS §6.
    //
    // The tick-box implementing clause 9.3 was built and then removed at the
    // client's request, so the digital-content exception cannot operate and
    // every buyer currently keeps the full 14-day right of withdrawal under
    // clause 9.2. That makes the honest wording here depend on a decision
    // nobody has taken yet: if the tick-box comes back, this block says the
    // right was waived; if the lawyer redrafts clause 9.3 out instead, it has
    // to set out a live 14-day right and how to exercise it — including the
    // model withdrawal form §2 calls for.
    //
    // Writing either version now would mean guessing which way that goes and
    // publishing a legal statement on the strength of it. A visible TODO is
    // the lesser harm, and `grep TODO` keeps it on the list.
    withdrawalTitle: "TODO — right of withdrawal (legal #10)",
    withdrawalConsent:
      "TODO — withdrawal wording; depends on whether clause 9.3 is implemented or redrafted (legal #10)",
    // Clause 1.4 makes purchase itself acceptance, so this is answerable —
    // but it sits in the same block as the two above and would read oddly
    // beside them. Fill in together.
    termsAcceptance: "TODO — terms acceptance wording (legal #7)",
  },

  myFarm: {
    title: "My farm",
    eyebrow: "Your memberships",
    intro: "Everything you hold, and everything the farm has posted about it.",
    emptyTitle: "You are not a member yet",
    emptyBody:
      "Become a member and you will follow the season through — sowing, weather, pests, harvest — in photographs and notes from the field.",
    browseParcels: "Browse the book",
    ownedSince: "Yours since {{date}}",
    latestUpdate: "Latest update",
    loginTitle: "Log in to see your farm",
    loginBody: "Use the same account you bought with.",

    // A membership whose viewer grant has not landed yet still belongs to the
    // buyer — it appears here with this state rather than being hidden, so
    // someone who has just paid never sees an empty page (SPEC §7.1).
    accessPendingLabel: "Access being set up",
    accessPendingBody:
      "This membership is yours and paid for. Someone at the farm is switching on your access to its record — we will email you the moment it is ready.",
    accessFailedLabel: "Access not switched on yet",
    accessFailedBody:
      "The membership is yours; only the access switch is outstanding. The team has been told and will finish it by hand.",
    accessLiveLabel: "Access live",
    openParcel: "Open the membership",
    postedOn: "Posted {{date}}",
    noUpdatesTitle: "Nothing from the field yet",
    noUpdatesBody:
      "The farm posts photographs and notes as the season goes on. The first usually lands within a fortnight.",
    photoCount: "{{count}} photographs",
    photoCountOne: "1 photograph",
    loadMore: "Show more memberships",
    errorTitle: "We could not load your farm",
    errorBody:
      "Your memberships are safe. This is almost always a passing connection problem — try again.",
    countOne: "1 membership",
    count: "{{count}} memberships",
  },

  admin: {
    title: "Operations",
    eyebrow: "Internal",
    intro: "Read-only. Vault content is edited in the Filedgr dashboard.",
    orders: "Orders",
    orderId: "Order",
    buyer: "Buyer",
    parcel: "Membership",
    amount: "Amount",
    placed: "Placed",
    paymentStatus: "Payment",
    grantStatus: "Access",
    grant: {
      pending: "Pending",
      granted: "Granted",
      failed: "Failed",
    },
    order: {
      created: "Awaiting payment",
      paid: "Paid",
      expired: "Expired",
      cancelled: "Cancelled",
      needs_refund: "Needs refund",
    },
    retryGrant: "Retry access grant",
    retryGrantRunning: "Retrying",
    retryGrantDone: "Access granted",
    retryGrantFailed: "Retry failed — see the error below",
    grantError: "Error",
    emptyTitle: "No orders yet",
    emptyBody: "Orders appear here the moment a Stripe payment completes.",
    forbiddenTitle: "Not your screen",
    forbiddenBody: "This account is not on the operations list.",

    // --- Queues ------------------------------------------------------------
    // SPEC §7.1: "awaiting manual grant" and "grant failed" are different
    // states with different meanings. The first is a healthy sale waiting on a
    // person; the second is an incident. Never merge them into one number.
    queueAwaiting: "Awaiting grant",
    queueAwaitingNote: "Paid, waiting for a person",
    queueFailed: "Grant failed",
    queueFailedNote: "Needs attention",
    queueGranted: "Access live",
    queueGrantedNote: "Nothing to do",
    queueTotal: "Orders",
    queueTotalNote: "All time",

    // --- Grant mode --------------------------------------------------------
    strategyLabel: "Grant mode",
    strategy: {
      manual: "By hand",
      partner: "Automatic — partner API",
      "webapp-jwt": "Automatic — service token",
    },
    strategyManualNote:
      "Grants are applied by a person in the Filedgr dashboard. Orders waiting below are normal sales, not incidents.",
    strategyAutoNote:
      "Grants are applied automatically after payment. Anything waiting below for long is worth a look.",

    filterLabel: "Show",
    filterAll: "All orders",
    filterAwaiting: "Awaiting grant",
    filterGranted: "Granted",
    filterFailed: "Failed",
    refresh: "Refresh",

    awaitingBadge: "Awaiting",
    vaultId: "Vault id",
    wallet: "Wallet",
    actions: "Actions",
    attempts: "Attempts: {{count}}",
    grantedByLine: "Recorded by {{who}}",
    paidOn: "Paid {{date}}",
    grantedOn: "Granted {{date}}",
    notApplicable: "—",

    markGranted: "Mark as granted",
    markGrantedRunning: "Recording",
    markGrantedDone: "Recorded",
    howToFulfil:
      "Grant VIEWER on this vault in the Filedgr dashboard using the buyer's email, then record it here.",
    retryUnavailable:
      "There is nothing to re-run while grants are applied by hand.",
    actionFailed: "That did not go through",

    copyEmail: "Copy buyer email",
    copyVaultId: "Copy vault id",
    copyWallet: "Copy buyer wallet address",

    scrollHint: "Scroll the table sideways for payment, access and actions.",
    filteredEmptyTitle: "Nothing in this queue",
    filteredEmptyBody:
      "No orders match this filter right now. Switch back to all orders to see everything.",
    errorTitle: "Operations could not be loaded",
    errorBody:
      "This screen only reads, so nothing is at risk. Try again in a moment.",
  },

  errors: {
    genericTitle: "Something went wrong",
    genericBody:
      "The page could not be loaded. It is usually temporary — try again, and tell us if it keeps happening.",
    networkTitle: "No connection",
    networkBody: "We could not reach the farm. Check your connection.",
    notFoundTitle: "Page not found",
    notFoundBody: "The link is wrong or the page has moved.",
    goHome: "Back to the farm",
    reload: "Reload the page",
    details: "Technical details",
    // Mapped from ApiError.code (src/shared/types/index.ts)
    PARCEL_UNAVAILABLE:
      "Someone took this membership first. It is no longer available.",
    PARCEL_NOT_FOUND: "That membership does not exist.",
    NO_PRICE: "This membership has no price set yet, so it cannot be bought.",
    UNAUTHENTICATED: "Please log in and try again.",
    FORBIDDEN: "This account is not allowed to do that.",
    loginFailed: "Login did not complete. Please try again.",
  },

  /**
   * ⚠️ LEGAL — the operative text of a binding contract. Supplied by the
   * client (LEGAL-BLOCKERS checklist #7). Reproduced verbatim.
   *
   * Rules for anyone editing this block:
   *  1. **Do not reword anything.** Not for tone, not for house style, not to
   *     fix a typo. Every string here is contract wording; changing it changes
   *     what the member agreed to. Corrections come from the client, in
   *     writing, and land here unaltered.
   *  2. **Do not reorder.** The clauses cross-reference each other by number
   *     ("in accordance with clause 8", "the price set out in clause 9"), and
   *     those numbers are rendered from the position of each key in the
   *     `SECTIONS` array in `containers/terms/index.tsx`. Insert a clause in
   *     the middle and every reference after it silently starts pointing at
   *     the wrong text. Cross-references are listed in that file.
   *  3. Keys are `s{section}c{clause}`, matching the rendered numbering, so a
   *     key can be found from a clause number and vice versa.
   *
   * Known drafting points, raised with the client, deliberately NOT fixed
   * here: "Burn Address" and "Benefits" are used as defined terms (clauses
   * 8.1, 8.2, 9.2, 9.3, 4.4) but are absent from the clause 2 definitions;
   * clause 4.5 spells the issuer "KuebeFarm".
   */
  terms: {
    metaTitle: "Terms and Conditions",
    metaDescription:
      "The terms on which Kuebefarm a.s.b.l. issues its Membership NFT, and on which a member holds it.",
    title: "Terms and Conditions of the Kuebefarm NFT",
    subtitle: "(the “Terms and Conditions”)",

    s1Title: "Introduction and acceptance",
    s1c1: "An NFT, or non-fungible token, is a cryptographic token recorded on a blockchain that represents a unique digital asset or an entitlement. Its defining feature is non-fungibility. Each token is unique and cannot be interchanged with another, by virtue of a single, non-replicable identifier.",
    s1c2: "The Kuebefarm Membership NFT consists of a unique digital token (the “Membership NFT”), which is acquired for a price and grants its Holder the membership and the benefits. It may be combined with, or provide access to, certain visual content or materials (the “Artwork”).",
    s1c3: "The Membership NFT is issued by Kuebefarm, a non-profit association (association sans but lucratif) established under the laws of the Grand Duchy of Luxembourg, with registered office at 40, rue Glesener, L-1630 Luxembourg, registered with the Luxembourg Business Register under number F15107 (“Kuebefarm”).",
    s1c4: "These Terms and Conditions constitute a legally binding agreement between Kuebefarm and the Holder, and govern the terms on which the Membership NFT is issued to and held by the Holder. By purchasing, minting, redeeming, accepting or otherwise holding a Kuebefarm Membership NFT, the Holder acknowledges that they have read, understood and agreed to be bound by these Terms and Conditions, and by the terms applicable to any third-party services used in connection with the NFT.",
    s1c5: "If for any reason the Holder does not agree with, or can no longer comply with, these Terms and Conditions, including the Kuebefarm Privacy Notice and the terms of any third-party services, the Holder must not accept the NFT or, if they already hold it, may surrender it in accordance with clause 8. This is without prejudice to the Holder’s rights under clause 9.",
    s1c6: "These Terms and Conditions, and any contract related to the acquisition, holding and use of the NFT between Kuebefarm and the Holder, are available in English.",
    s1c7: "Kuebefarm reserves the right to amend these Terms and Conditions, in particular to reflect changes in applicable law or regulation, in the features of the NFT, benefits, or in the technical arrangements. Kuebefarm may give the Holder prior notice of any material amendment and publish the amended version. The previous version will cease to apply to operations carried out with the NFT after the amended version has taken effect. No amendment shall apply retroactively or reduce rights the Holder has already acquired without the Holder’s consent.",
    s1c8: "Kuebefarm acts solely as issuer of the NFT and does not provide custody, exchange or any other regulated crypto-asset service. Any such services are provided by third parties, whose own terms apply.",
    s1c9: "The NFT is issued by being minted on the Blockchain to the Holder’s Wallet. Ownership is evidenced by the record on the Blockchain showing that the Holder's Wallet holds the corresponding token identifier. Kuebefarm retains the technical ability to burn or revoke the NFT in the circumstances set out in clause 8.4.",

    s2Title: "Definitions",
    s2Intro:
      "In these Terms and Conditions, the words in bold have the meanings set out below.",
    defArtworkTerm: "“Artwork”",
    defArtwork:
      "means the visual content or materials associated with a Membership NFT.",
    defBlockchainTerm: "“Blockchain”",
    defBlockchain:
      "means the Polygon network on which Kuebefarm issues or maintains the NFT and on which transactions relating to the NFT are validated, together with any additional network.",
    defSubjectTerm: "“Designated Subject”",
    defSubject:
      "means the specific subject depicted in, or associated with, a Membership NFT and identified in its Metadata or otherwise designated by Kuebefarm, together with the related Artwork and Living Page.",
    defHolderTerm: "“Holder”",
    defHolder:
      "means the person who acquires the Membership NFT from Kuebefarm and in whose Wallet it is recorded. Where the record on the Blockchain and Kuebefarm’s own records differ, Kuebefarm’s records prevail as between Kuebefarm and the Holder.",
    defIpTerm: "“Intellectual Property Rights”",
    defIp: "means copyrights and related rights, (including rights in computer software), patents, rights to inventions, trade marks, trade names, service marks, business names (including internet domain names), goodwill and the right to sue for passing off, design rights, database rights, rights in undisclosed or confidential information (such as know-how, trade secrets and inventions (whether patentable or not)), and all other intellectual property or similar proprietary rights of whatever nature (whether registered or not and including applications to register or rights to apply for registration and renewals or extensions of, and rights to claim priority from, such rights) which subsist anywhere in the world.",
    defKuebefarmTerm: "“Kuebefarm”",
    defKuebefarm: "has the meaning given in clause 1.3.",
    defLivingPageTerm: "“Living Page”",
    defLivingPage:
      "means the evolving record associated with a Membership NFT, which may include the Artwork, the place and date of issuance, information on the Designated Subject, and updates published over time.",
    defMetadataTerm: "“Metadata”",
    defMetadata:
      "means the data and attributes associated with a Membership NFT that describe and identify it and its characteristics. This may include, without limitation, its unique token identifier, a reference or link to the Artwork, the Designated Subject, and the associated benefits, whether recorded on-chain or stored off-chain.",
    defNftTerm: "“NFT” or “Membership NFT”",
    defNft:
      "means the unique, non-fungible token issued by Kuebefarm on the Blockchain by means of the Smart Contract, associated with the related Artwork, Living Page, Designated Subject, and Metadata.",
    defSmartContractTerm: "“Smart Contract”",
    defSmartContract:
      "means the self-executing code deployed on the Blockchain by or on behalf of Kuebefarm that governs the issuance, recording, and functioning of the NFT.",
    defThirdPartyTerm: "“Third-Party Services”",
    defThirdParty:
      "means any services not provided by Kuebefarm but used in connection with the acquisition, holding, or use of the NFT, including wallet, minting, or payment services.",
    defWalletTerm: "“Wallet”",
    defWallet:
      "means the digital wallet in which the NFT is recorded, whether controlled directly by the Holder or made available to the Holder through a service provider engaged by Kuebefarm.",

    s3Title: "Conditions",
    s3c1: "The Holder may acquire and hold a Membership NFT only if the Holder: (i) has the right and authority, in accordance with relevant applicable laws and regulations, to enter into these Terms and Conditions; and (ii) is fully able and competent to satisfy the terms, conditions, and obligations set out in them. By acquiring or holding a Membership NFT, the Holder represents and warrants that they meet these conditions and are able lawfully to hold the NFT.",
    s3c2: "The Holder acknowledges that, in some countries and for certain persons or entities, the acquisition, holding, or use of NFTs may be restricted or prohibited under applicable law. The Holder is responsible for ensuring that acquiring and holding the NFT is lawful for them, and Kuebefarm accepts no liability where the Holder’s ability to acquire, hold or use the NFT is limited or restricted by such laws.",

    s4Title: "Offer and acceptance of the NFT",
    s4c1: "Kuebefarm may offer the Membership NFT to eligible persons who wish to acquire it and who provide Kuebefarm, or the platform made available for that purpose, with the information required to complete the acquisition, including a compatible wallet address and any personal information reasonably necessary to facilitate the issuance (each an \"Offer\").",
    s4c2: "To accept an Offer and receive a Membership NFT, the Holder must pay the price set out in clause 9 and hold a Wallet compatible with the Blockchain. The Offer is accepted, and these Terms and Conditions become binding, when the Membership NFT is minted or transferred to the Holder’s Wallet.",
    s4c3: "The price stated in clause 9.1 is the only amount payable by the Holder to Kuebefarm.",
    s4c4: "The NFT is not designed to hold or store value, and Kuebefarm makes no representation as to its value, present or future. The price paid under clause 9.1 is consideration for the membership and the benefits.",
    s4c5: "KuebeFarm may require the Holder to provide additional information or documents from time to time, including where KuebeFarm has reason to believe the Holder is in breach of these Terms and Conditions. In such cases KuebeFarm may decline to issue the NFT (before acceptance) or terminate these Terms and Conditions in accordance with clause 8.",

    s5Title: "Rights",
    s5c1Lead: "Ownership.",
    s5c1: "The Holder owns the unique NFT recorded in the Holder’s Wallet. The rights the Holder has in the Artwork are limited to those expressly set out in clause 6, and Kuebefarm reserves any right not expressly granted.",
    s5c2Lead: "No economic rights.",
    s5c2: "The NFT confers no ownership of the Designated Subject or of any physical asset, no produce, no share of profit or revenue, no dividend or interest, no right to redemption for cash, and no claim over the assets of Kuebefarm. The NFT is not an investment, and carries no expectation of any increase in value.",
    s5c3Lead: "No governance.",
    s5c3: "The NFT confers no voting or governance right over Kuebefarm.",

    s6Title: "Intellectual property",
    s6c1Lead: "Ownership.",
    s6c1: "Kuebefarm owns all intellectual property rights in the Artwork and the Living Page. The rights the Holder has in the Artwork are limited to those expressly set out in this clause, and Kuebefarm reserves any right not expressly granted.",
    s6c2Lead: "Licensed rights.",
    s6c2: "The Artwork is made available to the Holder solely for the Holder’s own personal, non-commercial enjoyment. Kuebefarm grants the Holder a worldwide, non-exclusive, revocable, non-transferable, non-sub-licensable licence to display the Artwork solely for the Holder’s own personal, non-commercial use, including display on the Holder's personal social media accounts and as a personal profile image, provided the Holder does not present the Artwork as their own work.",
    s6c3: "Except as permitted by clause 6.2, the Holder may not sell, transfer to any third party (whether as a gift or for money), reproduce, distribute, copy, duplicate, resell, modify, publicly display or perform, prepare derivative works based on, frame, mirror, repost or otherwise exploit the Artwork for any commercial purpose, attempt to register any intellectual property right in it, or use it in connection with hateful, violent or unlawful content or in any way that harms Kuebefarm. All rights not expressly granted are reserved.",

    s7Title: "The living NFT and benefits",
    s7c1: "The Artwork, Metadata, and Living Page may be updated over time and are stored partly off-chain. Kuebefarm does not guarantee their permanent, uninterrupted or perpetual availability.",
    s7c2: "The benefits attached to the NFT are described by Kuebefarm when offered. They are non-financial, have no cash value, are not redeemable for cash, and may reasonably be modified or withdrawn.",

    s8Title: "Transferability, surrender and termination",
    s8c1Lead: "Non-transferable.",
    s8c1: "The NFT is non-transferable. The Holder may not sell, licence, trade, donate, give away or otherwise transfer or dispose of the NFT to any third party for any reason, whether or not for value, and no resale royalty applies. The Smart Contract restricts transfers accordingly. This clause does not prevent a transfer to Kuebefarm or to the Burn Address under clause 8.2 or 8.4. Any purported transfer to a third party is void as against Kuebefarm, and the purported transferee acquires no Membership, no Benefits and no licence under clause 6.",
    s8c2Lead: "Surrender.",
    s8c2: "The Holder may at any time surrender the NFT by returning it to Kuebefarm or by transferring it to the applicable Polygon burn address. Membership and the associated benefits then lapse.",
    s8c3Lead: "Termination.",
    s8c3: "These Terms and Conditions, the Holder’s membership and the licence granted under clause 6 terminate automatically if the Holder: (i) sells, transfers, disposes of or attempts to transfer the NFT to any third party; or (ii) materially breaches these Terms and Conditions, or any applicable law or regulation.",
    s8c4Lead: "Consequences.",
    s8c4: "On surrender or termination, the Holder’s membership and licence end, and the Holder must, within a reasonable period, either return the NFT to Kuebefarm or transfer it to the Polygon Burn Address, and confirm to Kuebefarm in writing that they have done so. Where the Holder materially breaches these Terms and Conditions or applicable law, Kuebefarm may burn or revoke the NFT. Burning an NFT is irreversible: once burned, the NFT cannot be reinstated, and Kuebefarm is not required to provide a replacement, including if the Holder later changes their mind.",

    s9Title: "Price, taxes, and consumer rights",
    s9c1: "The price of the NFT is EUR 50, inclusive of any applicable value added tax, payable at the time of acquisition through Kuebefarm’s payment provider. The price is consideration for the Membership and the Benefits and does not represent stored monetary value.",
    s9c2: "Where the Holder is a consumer, the Holder has the right to withdraw from this contract within fourteen days of its conclusion, without giving any reason, by notifying Kuebefarm. Kuebefarm will refund the price paid within fourteen days of being so notified, using the same means of payment. The Holder must, within the same period, return the NFT to Kuebefarm or transfer it to the Burn Address.",
    s9c3: "The Holder may ask Kuebefarm to issue the NFT before the withdrawal period has expired. Where the Holder expressly requests this and acknowledges that the NFT is issued immediately and irreversibly, the Holder’s right of withdrawal is lost once the NFT has been issued. Kuebefarm will record that request and that acknowledgement.",
    s9c4: "Nothing in these Terms and Conditions affects the Holder’s mandatory rights as a consumer under Luxembourg law, including the statutory guarantee of conformity.",

    s10Title: "Personal Data",
    s10c1: "In connection with the acquisition, holding, and use of the NFT, Kuebefarm processes personal data relating to the Holder. This includes the Holder’s email address, first name and last name, and, where provided, a billing address. This data is collected through Kuebefarm's service providers: payment and checkout information (name, email address and billing address) is collected through Stripe, and the Holder’s wallet and email address are managed through Filedgr SARL, each acting as a processor or sub-processor on Kuebefarm's behalf.",
    s10c2: "Kuebefarm Privacy Notice sets out what personal data is processed, the purposes and legal basis for processing, the recipients of the data, and the Holder’s rights, including the right to lodge a complaint.",
    s10c3: "The Holder acknowledges that information recorded on the Blockchain, including the wallet address and token identifier, is public and, by the nature of the technology, cannot be altered or erased.",

    s11Title: "Warranties, liability and assumption of risk",
    s11c1: "To the maximum extent permitted by law, the NFT and any associated content are provided “as is” and “as available”, with no warranties of any kind.",
    s11c2: "The Holder acknowledges the risks of distributed-ledger technology, including that transactions are irreversible, that changes or forks to the Polygon network may have adverse effects, and that the regulatory regime for crypto-assets is uncertain and may affect the NFT. Where the Holder controls the Wallet directly, the Holder is solely responsible for it and for the associated private keys, and loss of a key means permanent loss of access to the NFT. Where the Wallet is made available through a service provider, the terms of that provider apply to its security and availability, and Kuebefarm is not responsible for that provider's acts or omissions save as required by law.",
    s11c3: "The Holder confirms that they have sufficient knowledge and understanding of distributed-ledger technology, NFTs, and digital wallets to make an informed decision to acquire the NFT, and that they are solely responsible for assessing the nature and appropriateness of these risks.",
    s11c4: "To the maximum extent permitted by law, Kuebefarm’s liability under or in connection with these Terms and Conditions is limited. Nothing in these Terms and Conditions excludes or limits any liability that cannot be excluded or limited by law.",

    s12Title: "Indemnity and general provisions",
    s12c1: "The Holder will indemnify Kuebefarm against reasonable claims, losses and expenses arising from the Holder’s breach of these Terms and Conditions, the Holder’s unlawful use of the NFT or the Artwork, or the Holder's infringement of the rights of any third party. This indemnity does not apply to the extent the loss results from Kuebefarm’s own negligence or breach.",
    s12c2: "These Terms and Conditions constitute the entire agreement between Kuebefarm and the Holder in respect of the NFT.",
    s12c3: "If any provision of these Terms and Conditions is held to be invalid or unenforceable, the remaining provisions continue in full force and effect.",
    s12c4: "These Terms and Conditions may be amended in accordance with clause 1.7.",
    s12c5: "These Terms and Conditions are governed by the laws of the Grand Duchy of Luxembourg, and the courts of Luxembourg have jurisdiction, subject to any mandatory consumer-protection rules that may entitle a consumer to bring proceedings in another forum.",

    closing:
      "By purchasing, minting, accepting or holding a Kuebefarm Membership NFT, the Holder confirms that they have read, understood and accepted these Terms.",
  },

  seo: {
    titleTemplate: "{{title}} — Kuebefarm",
    defaultTitle: "Kuebefarm — Be a part of the farm",
    defaultDescription:
      "Kuebefarm draws named memberships of its urban farm on the Kirchberg plateau in Luxembourg. Each membership is one of a kind, registered on-chain, and followed through the season in photographs and field notes.",
    parcelsDescription:
      "Every Kuebefarm membership currently in the book — each one drawn once, named once, and held by a single member on the Kirchberg plateau.",
    parcelFallbackDescription:
      "{{name}} — a membership of the Kuebefarm urban farm on the Kirchberg plateau in Luxembourg, followed through the season in photographs and field notes.",
  },
} as const;

export type Dictionary = typeof en;
